import * as Popover from "@radix-ui/react-popover"

import { clsx } from "clsx"
import { useCallback, useEffect, useRef, useState } from "react"
import { isElectronApp } from "@/utils/electron"

type ContentTooltipProps = Popover.PopoverContentProps & {
	open: boolean
	children: React.ReactNode
	tooltip?: string
	tooltipMaxWidth?: number
	tooltipDelay?: number
}

export const ContentTooltip = ({
	children,
	open,
	tooltip,
	tooltipMaxWidth,
	...props
}: ContentTooltipProps) => {
	const collisionPadding = isElectronApp ? { top: 38, right: 8, bottom: 8, left: 8 } : 8
	return (
		<Popover.Root open={open}>
			<Popover.Anchor asChild>{children}</Popover.Anchor>
			<Popover.Portal>
				{tooltip && (
					<Popover.Content
						sideOffset={10}
						style={{
							whiteSpace: "pre-line",
							maxWidth: tooltipMaxWidth ? `${tooltipMaxWidth}px` : "auto",
						}}
						className={clsx(
							`relative z-50 text-wrap rounded-sm bg-gray-300 px-1.5 py-1 text-left text-xs text-black shadow-xl`,
						)}
						collisionPadding={collisionPadding}
						{...props}
					>
						{tooltip}
					</Popover.Content>
				)}
			</Popover.Portal>
		</Popover.Root>
	)
}

export const ContentTooltipWrapper = (props: {
	children: React.ReactNode
	tooltip?: string
	tooltipMaxWidth?: number
	tooltipDelay?: number
	tooltipProps?: Popover.PopoverContentProps
	allowOpen?: boolean
}) => {
	const { children, tooltip, tooltipMaxWidth, tooltipDelay } = props
	const allowOpen = props.allowOpen ?? true

	const [showTooltip, setShowTooltip] = useState(false)
	const showTooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const clearShowTooltipTimeout = useCallback(() => {
		if (showTooltipTimeoutRef.current) {
			clearTimeout(showTooltipTimeoutRef.current)
			showTooltipTimeoutRef.current = null
		}
	}, [])

	useEffect(() => {
		return () => {
			clearShowTooltipTimeout()
		}
	}, [clearShowTooltipTimeout])

	useEffect(() => {
		if (!allowOpen) {
			clearShowTooltipTimeout()
			setShowTooltip(false)
		}
	}, [allowOpen, clearShowTooltipTimeout])

	useEffect(() => {
		const hideTooltip = () => {
			clearShowTooltipTimeout()
			setShowTooltip(false)
		}

		const handleVisibilityChange = () => {
			if (document.hidden) {
				hideTooltip()
			}
		}

		window.addEventListener("blur", hideTooltip)
		document.addEventListener("visibilitychange", handleVisibilityChange)

		return () => {
			window.removeEventListener("blur", hideTooltip)
			document.removeEventListener("visibilitychange", handleVisibilityChange)
		}
	}, [clearShowTooltipTimeout])

	return (
		<ContentTooltip
			open={allowOpen && showTooltip}
			tooltip={tooltip}
			tooltipMaxWidth={tooltipMaxWidth}
			{...props.tooltipProps}
		>
			<div
				onMouseEnter={() => {
					if (!allowOpen || !tooltip) return

					clearShowTooltipTimeout()
					showTooltipTimeoutRef.current = setTimeout(() => {
						showTooltipTimeoutRef.current = null
						setShowTooltip(true)
					}, tooltipDelay ?? 500)
				}}
				onMouseLeave={() => {
					clearShowTooltipTimeout()
					setShowTooltip(false)
				}}
			>
				{children}
			</div>
		</ContentTooltip>
	)
}
